import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ProductionReport } from '../utils/types';
import {
  getProductionReportById,
  postProductionReport,
} from './production-report.thunk';
import type { ProductionReportResponse } from '../api/production-report.api';

interface ProductionReportState {
  fetchReportLoading: boolean;
  fetchReportError?: string | null;
  reportData?: ProductionReport | null;
  isSubmittingReport: boolean;
  reportingError?: string | null;
  reportingResponse?: ProductionReportResponse | null;
}

const initialState: ProductionReportState = {
  fetchReportLoading: false,
  fetchReportError: null,
  reportData: null,
  isSubmittingReport: false,
  reportingError: null,
  reportingResponse: null,
};

const productionReportSlice = createSlice({
  name: 'productionReport',
  initialState,
  reducers: {
    resetReportState(state) {
      state.fetchReportLoading = false;
      state.fetchReportError = null;
      state.reportData = null;
      state.isSubmittingReport = false;
      state.reportingError = null;
      state.reportingResponse = null;
    },
  },
  extraReducers: builder => {
    builder
      // Handle fetch production report pending state
      .addCase(getProductionReportById.pending, state => {
        state.fetchReportLoading = true;
        state.fetchReportError = null;
        state.reportData = null;
      })
      .addCase(
        getProductionReportById.fulfilled,
        (state, action: PayloadAction<ProductionReport>) => {
          state.reportData = action.payload;
          state.fetchReportLoading = false;
          state.fetchReportError = null;
        }
      )
      .addCase(getProductionReportById.rejected, (state, action) => {
        state.fetchReportLoading = false;
        state.reportData = null;
        state.fetchReportError =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch production report';
      })
      .addCase(postProductionReport.pending, state => {
        state.isSubmittingReport = true;
        state.reportingError = null;
        state.reportingResponse = null;
      })
      .addCase(
        postProductionReport.fulfilled,
        (state, action: PayloadAction<ProductionReportResponse>) => {
          state.reportingResponse = action.payload;
          state.isSubmittingReport = false;
          state.reportingError = null;
        }
      )
      .addCase(postProductionReport.rejected, (state, action) => {
        state.isSubmittingReport = false;
        state.reportingResponse = null;
        state.reportingError =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to report production';
      });
  },
});

export const { resetReportState } = productionReportSlice.actions;
export default productionReportSlice.reducer;
